/**
 * Face ID — Continuous Detection
 *
 * Runs face detection continuously against a webcam feed.
 * Matches detected faces against the IndexedDB face database.
 * Accumulates detection cards grouped by person name.
 * Publishes match/unknown/lost events and telemetry over MQTT.
 */

import * as H from './dist/human.esm.js';
import * as indexDb from './indexdb.js';
import { getStationId, renderStationBadge } from './station.js';
import * as mq from './mqtt.js';

// ── Wake Lock (prevent sleep) ──

let wakeLock: any = null;
async function requestWakeLock() {
  try {
    if ('wakeLock' in navigator) {
      wakeLock = await (navigator as any).wakeLock.request('screen');
      wakeLock.addEventListener('release', () => { wakeLock = null; });
    }
  } catch (_e) { /* wake lock not supported or denied */ }
}
document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') requestWakeLock(); });

// ── Mobile Detection ──

const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) || (navigator.maxTouchPoints > 0 && window.innerWidth < 768);

// ── Configuration ──

const humanConfig = {
  cacheSensitivity: 0.01,
  modelBasePath: './models',
  filter: { enabled: true, equalization: true },
  debug: false,
  face: {
    enabled: true,
    detector: { rotation: true, return: true, mask: false },
    description: { enabled: true },
    iris: { enabled: true },
    emotion: { enabled: false },
    antispoof: { enabled: true },
    liveness: { enabled: true },
  },
  body: { enabled: false },
  hand: { enabled: false },
  object: { enabled: false },
  gesture: { enabled: true },
};

const matchOptions = { order: 2, multiplier: 25, min: 0.2, max: 0.8 };
let liteMode = false;

function toggleLiteMode() {
  liteMode = !liteMode;
  human.config.face.mesh = { enabled: !liteMode };
  human.config.face.iris = { enabled: !liteMode };
  human.config.face.antispoof = { enabled: !liteMode };
  human.config.face.liveness = { enabled: !liteMode };
  human.config.face.detector.rotation = !liteMode;
  human.config.gesture = { enabled: !liteMode };
  dom.liteBtn.innerText = liteMode ? 'full' : 'lite';
  dom.liteBtn.classList.toggle('active', liteMode);
  console.log('faceid: mode', liteMode ? 'lite (blazeface + faceres only)' : 'full');
}
let matchThreshold = 0.5;

// ── Types ──

interface FaceCard {
  key: string;
  name: string;
  count: number;
  similarity: number;
  el: HTMLDivElement;
  canvasEl: HTMLCanvasElement;
  countEl: HTMLDivElement;
  simEl: HTMLDivElement;
}

// ── State ──

const human = new H.Human(humanConfig);
human.draw.options.font = 'small-caps 18px "Lato"';
human.draw.options.lineHeight = 20;

let db: indexDb.FaceRecord[] = [];
let running = true;
const cards: Map<string, FaceCard> = new Map();
const timestamp = { detect: 0, draw: 0 };
let lastTelemetry = 0;
const TELEMETRY_INTERVAL = 2000; // ms
let telemetryCounters = { matched: 0, unknown: 0 };

// ── MQTT Publishing Mode ──
// "smart" = only publish on state transitions (default)
// "stream" = publish every detection frame
let mqttMode: 'smart' | 'stream' = 'smart';
type DetectState = 'idle' | 'unknown' | `matched:${string}`;
let prevState: DetectState = 'idle';

// ── DOM ──

const dom = {
  video: document.getElementById('video') as HTMLVideoElement,
  canvas: document.getElementById('canvas') as HTMLCanvasElement,
  status: document.getElementById('status') as HTMLDivElement,
  faceTray: document.getElementById('faceTray') as HTMLDivElement,
  fps: document.getElementById('fps') as HTMLDivElement,
  clearCards: document.getElementById('clearCards') as HTMLButtonElement,
  loadingOverlay: document.getElementById('loadingOverlay') as HTMLDivElement,
  stationBadge: document.getElementById('stationBadge') as HTMLDivElement,
  emptyDbModal: document.getElementById('emptyDbModal') as HTMLDivElement,
  emptyDbDismiss: document.getElementById('emptyDbDismiss') as HTMLButtonElement,
  liteBtn: document.getElementById('liteBtn') as HTMLButtonElement,
};

// ── Webcam ──

async function startWebcam() {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: false,
    video: { facingMode: 'user', resizeMode: 'none' as any, width: { ideal: isMobile ? 640 : 1280 } },
  });
  const ready = new Promise(r => { dom.video.onloadeddata = () => r(true); });
  dom.video.srcObject = stream;
  void dom.video.play();
  await ready;
  dom.canvas.width = dom.video.videoWidth;
  dom.canvas.height = dom.video.videoHeight;
}

// ── Mirror Utilities ──

function mirrorResults(result: any, width: number) {
  const faces = result.face || [];
  const mirrored = faces.map((face: any) => {
    const f = { ...face };
    if (f.box) f.box = [width - f.box[0] - f.box[2], f.box[1], f.box[2], f.box[3]];
    if (f.boxRaw) f.boxRaw = [1 - f.boxRaw[0] - f.boxRaw[2], f.boxRaw[1], f.boxRaw[2], f.boxRaw[3]];
    if (f.mesh) f.mesh = f.mesh.map((p: number[]) => [width - p[0], p[1], p[2]]);
    if (f.meshRaw) f.meshRaw = f.meshRaw.map((p: number[]) => [1 - p[0], p[1], p[2]]);
    if (f.annotations) {
      f.annotations = {};
      for (const k of Object.keys(face.annotations)) {
        const pts = face.annotations[k];
        f.annotations[k] = Array.isArray(pts) ? pts.map((p: number[] | null) => p ? [width - p[0], p[1], p[2]] : p) : pts;
      }
    }
    if (f.rotation?.angle) f.rotation = { ...f.rotation, angle: { ...f.rotation.angle, yaw: -f.rotation.angle.yaw, roll: -f.rotation.angle.roll } };
    if (f.rotation?.gaze) f.rotation = { ...f.rotation, gaze: { ...f.rotation.gaze, bearing: -f.rotation.gaze.bearing } };
    return f;
  });
  return { ...result, face: mirrored };
}

// ── Face Cards ──

function clearAllCards() {
  cards.clear();
  dom.faceTray.innerHTML = '';
}

function createCard(key: string, name: string, isMatch: boolean, dbRecord?: indexDb.FaceRecord): FaceCard {
  const el = document.createElement('div');
  el.className = 'fc';

  const canvasEl = document.createElement('canvas');
  canvasEl.className = 'fc-live';
  canvasEl.width = 120;
  canvasEl.height = 160;
  el.appendChild(canvasEl);

  if (isMatch && dbRecord?.image) {
    const refCanvas = document.createElement('canvas');
    refCanvas.className = 'fc-ref';
    refCanvas.width = dbRecord.image.width;
    refCanvas.height = dbRecord.image.height;
    refCanvas.getContext('2d')?.putImageData(dbRecord.image, 0, 0);
    el.appendChild(refCanvas);
  }

  const overlay = document.createElement('div');
  overlay.className = 'fc-overlay';

  const nameEl = document.createElement('div');
  nameEl.className = `fc-name ${isMatch ? 'matched' : 'unknown'}`;
  nameEl.innerText = name;

  const bottomRow = document.createElement('div');
  bottomRow.className = 'fc-bottom';

  const countEl = document.createElement('div');
  countEl.className = 'fc-count';
  countEl.innerText = '1';

  const simEl = document.createElement('div');
  simEl.className = 'fc-sim';
  simEl.innerText = '';

  bottomRow.appendChild(countEl);
  bottomRow.appendChild(simEl);
  overlay.appendChild(nameEl);
  overlay.appendChild(bottomRow);
  el.appendChild(overlay);
  dom.faceTray.prepend(el);

  return { key, name, count: 1, similarity: 0, el, canvasEl, countEl, simEl };
}

function flashCard(card: FaceCard) {
  card.el.classList.remove('flash');
  void card.el.offsetWidth;
  card.el.classList.add('flash');
}

// ── Draw Loop ──

async function drawLoop() {
  if (!running) return;

  const interpolated = human.next(human.result);
  const ctx = dom.canvas.getContext('2d');
  if (ctx) {
    ctx.save();
    ctx.translate(dom.canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(dom.video, 0, 0, dom.canvas.width, dom.canvas.height);
    ctx.restore();
  }

  const mirrored = mirrorResults(interpolated, dom.canvas.width);
  await human.draw.all(dom.canvas, mirrored);

  const now = human.now();
  if (timestamp.draw > 0) {
    const drawFps = Math.round(10000 / (now - timestamp.draw)) / 10;
    const detectFps = timestamp.detect > 0 ? Math.round(10000 / (now - timestamp.detect)) / 10 : 0;
    dom.fps.innerText = `${detectFps} / ${drawFps} fps`;

    // Periodic telemetry
    if (now - lastTelemetry > TELEMETRY_INTERVAL) {
      mq.publishTelemetry({
        fps: drawFps,
        detectFps,
        faces: human.result?.face?.length || 0,
        matched: telemetryCounters.matched,
        unknown: telemetryCounters.unknown,
      });
      telemetryCounters = { matched: 0, unknown: 0 };
      lastTelemetry = now;
    }
  }
  timestamp.draw = now;
  requestAnimationFrame(drawLoop);
}

// ── Detection Loop ──

async function detectLoop() {
  if (!running) return;

  const prevTensor = human.result?.face?.[0]?.tensor;
  if (prevTensor) human.tf.dispose(prevTensor);

  await human.detect(dom.video);
  timestamp.detect = human.now();

  const face = human.result.face[0];
  if (face && face.embedding && face.embedding.length > 0) {
    let key = 'unknown';
    let name = 'Unknown';
    let isMatch = false;
    let similarity = 0;
    let matchedRecord: indexDb.FaceRecord | undefined;

    if (db.length > 0) {
      const descriptors = db.map(r => r.descriptor).filter(d => d.length > 0);
      if (descriptors.length > 0) {
        const res = human.match.find(face.embedding, descriptors, matchOptions);
        similarity = Math.round(res.similarity * 1000) / 10;
        if (res.similarity > matchThreshold && db[res.index]) {
          key = `name-${db[res.index].name}`;
          name = db[res.index].name;
          isMatch = true;
          matchedRecord = db[res.index];
        }
      }
    }

    // Update status
    if (isMatch) {
      dom.status.className = 'status-pill matched';
      dom.status.innerText = `matched: ${name}`;
    } else if (db.length === 0) {
      dom.status.className = 'status-pill unknown';
      dom.status.innerText = 'no faces in db';
    } else {
      dom.status.className = 'status-pill unknown';
      dom.status.innerText = 'unknown face';
    }

    // Determine current state
    const curState: DetectState = isMatch ? `matched:${name}` : 'unknown';
    const stateChanged = curState !== prevState;

    // Publish MQTT event (smart = only on transition, stream = every frame)
    const shouldPublish = mqttMode === 'stream' || stateChanged;

    if (isMatch) {
      telemetryCounters.matched++;
      if (shouldPublish) {
        mq.publishEvent('match', {
          name,
          similarity,
          faceScore: Math.round((face.faceScore || 0) * 100),
          real: Math.round((face.real || 0) * 100),
          live: Math.round((face.live || 0) * 100),
          age: Math.round(face.age || 0),
          gender: face.gender || '',
          genderScore: Math.round((face.genderScore || 0) * 100),
        });
      }
    } else {
      telemetryCounters.unknown++;
      if (shouldPublish) {
        mq.publishEvent('unknown', {
          similarity,
          faceScore: Math.round((face.faceScore || 0) * 100),
          real: Math.round((face.real || 0) * 100),
          live: Math.round((face.live || 0) * 100),
        });
      }
    }
    prevState = curState;

    // Update or create card
    let card = cards.get(key);
    if (!card) {
      card = createCard(key, name, isMatch, matchedRecord);
      cards.set(key, card);
    } else {
      card.count++;
      card.countEl.innerText = `${card.count}`;
    }
    card.similarity = similarity;
    card.simEl.innerText = db.length > 0 ? `${similarity}%` : '';
    flashCard(card);

    // Update live thumbnail
    if (face.tensor) {
      try { await human.tf.browser.toPixels(face.tensor as any, card.canvasEl); }
      catch (_e) { /* tensor may be disposed between frames */ }
    }
  } else {
    // No face detected
    if (prevState !== 'idle') {
      mq.publishEvent('lost', {});
      prevState = 'idle';
    }
    dom.status.className = 'status-pill searching';
    dom.status.innerText = 'searching...';
  }

  requestAnimationFrame(detectLoop);
}

// ── MQTT Command Handlers ──

function handleCommand(cmd: string, payload: any) {
  switch (cmd) {
    case 'clear':
      console.log('mqtt: cmd clear');
      clearAllCards();
      break;
    case 'reload':
      console.log('mqtt: cmd reload');
      indexDb.load().then(records => {
        db = records;
        console.log('mqtt: reloaded', db.length, 'face records');
      });
      break;
    case 'threshold':
      if (payload?.value != null) {
        matchThreshold = Number(payload.value);
        console.log('mqtt: threshold set to', matchThreshold);
      }
      break;
    case 'mode':
      if (payload?.value === 'smart' || payload?.value === 'stream') {
        mqttMode = payload.value;
        console.log('mqtt: mode set to', mqttMode);
      }
      break;
    case 'pause':
      if (running) {
        running = false;
        dom.status.className = 'status-pill searching';
        dom.status.innerText = 'paused';
        console.log('mqtt: paused');
      }
      break;
    case 'resume':
      if (!running) {
        running = true;
        detectLoop();
        console.log('mqtt: resumed');
      }
      break;
    default:
      console.log('mqtt: unknown cmd', cmd, payload);
  }
}

// ── Init ──

async function init() {
  console.log('faceid: version', human.version, isMobile ? '(mobile mode)' : '(desktop mode)');
  requestWakeLock();

  // Station ID badge
  renderStationBadge(dom.stationBadge);

  await startWebcam();
  await human.load();
  await human.warmup();

  db = await indexDb.load();
  console.log('faceid: loaded', db.length, 'face records');

  // Connect MQTT
  try {
    await mq.connect();
    mq.publishInfo({ version: human.version, threshold: matchThreshold, dbSize: db.length, mode: mqttMode });
    mq.onCommand(handleCommand);
    console.log('faceid: mqtt connected, station', getStationId());
  } catch (e) {
    console.warn('faceid: mqtt connection failed, running without mqtt', e);
  }

  dom.loadingOverlay.classList.add('hidden');
  dom.status.innerText = 'searching...';

  dom.clearCards.addEventListener('click', clearAllCards);
  dom.liteBtn.addEventListener('click', toggleLiteMode);

  // Show empty DB modal if no faces enrolled
  if (db.length === 0) {
    dom.emptyDbModal.style.display = 'flex';
    dom.emptyDbDismiss.addEventListener('click', () => {
      dom.emptyDbModal.style.display = 'none';
    });
  }

  detectLoop();
  drawLoop();
}

window.onload = init;
